// Step 5: a name, how many tasks may run at once, and everything chosen so far, each with a
// way back to change it.
import { PencilSimple } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Input, Switch } from "rizzui";

import { useT } from "../../../i18n";
import { NetworkFields } from "../../../network/NetworkFields";
import { Hint } from "../../../ui/SideDrawer";
import { Mono } from "../../../ui/status";
import { cadenceLabel } from "../../display";
import { marketplaceTypesQuery } from "../../lookups";
import { cadenceMinutes } from "../cadence";
import { type Credential, credentialRequirements, type Draft, jqlOf, MAX_PARALLEL, type StepKey, STEPS } from "../draft";
import { wireRepositories } from "../repositories";

function Line({ label, step, onEdit, children }: { label: string; step: StepKey; onEdit?: (i: number) => void; children: ReactNode }) {
  const t = useT().tasks.create.review;
  return (
    <div className="flex flex-wrap items-start gap-x-4 gap-y-1 py-3 first:pt-0 last:pb-0 sm:flex-nowrap">
      <dt className="w-full shrink-0 pt-0.5 text-xs uppercase tracking-wider text-gray-500 sm:w-40">{label}</dt>
      <dd className="m-0 min-w-0 flex-1 break-words text-sm text-gray-900">{children}</dd>
      {onEdit ? (
        <button type="button" onClick={() => onEdit(STEPS.indexOf(step))} className="inline-flex shrink-0 items-center gap-1 text-[13px] font-medium text-primary hover:underline" aria-label={t.editLabel(label)}>
          <PencilSimple className="h-3.5 w-3.5" aria-hidden /> {t.edit}
        </button>
      ) : null}
    </div>
  );
}

function filterText(d: Draft): ReactNode {
  if (d.source === "jira") return <Mono className="whitespace-pre-wrap [font-variant-ligatures:none]">{jqlOf(d)}</Mono>;
  if (d.source === "github") return <Mono>{d.github.repo.trim()}</Mono>;
  if (d.source === "asana") return <Mono>{d.asana.project.trim()}</Mono>;
  return null;
}

interface Props {
  draft: Draft;
  onChange: (patch: Partial<Draft>) => void;
  credentials: Credential[];
  /** Back to a step, by its index in `STEPS`. */
  onEdit: (i: number) => void;
  /** The steps that can be revisited (editing a group: not its type or workflow). */
  editable?: readonly StepKey[];
}

export function ReviewStep({ draft, onChange, credentials, onEdit, editable = STEPS }: Props) {
  const { tasks, vpn, workflows } = useT();
  const t = tasks.create.review;
  const toolchainNames = workflows.needs.requirements.toolchainNames;
  const types = useQuery(marketplaceTypesQuery());
  const typeName = types.data?.find((x) => x.id === draft.taskTypeId)?.name ?? draft.taskTypeId;
  const secretName = (id: string) => (id === "platform" ? tasks.create.access.platformKey : credentials.find((c) => c.id === id)?.name ?? id);
  const bound = credentialRequirements(draft.workflow).filter(({ index }) => draft.bindings[index]);
  const repositories = wireRepositories(draft.repositories);
  const edit = (step: StepKey) => (editable.includes(step) ? onEdit : undefined);
  return (
    <div className="space-y-7">
      <div className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_10rem]">
        <div>
          <Input label={t.name} value={draft.name} maxLength={200} onChange={(e) => onChange({ name: e.target.value })} />
          <Hint>{t.nameHint}</Hint>
        </div>
        <div>
          <Input label={t.parallel} type="number" min={1} max={MAX_PARALLEL} value={String(draft.maxParallel)} onChange={(e) => onChange({ maxParallel: Number(e.target.value) })} />
          <Hint>{t.parallelHint}</Hint>
        </div>
      </div>
      <div>
        <Switch
          role="switch"
          label={vpn.group.requires}
          checked={draft.requiresVpn}
          onChange={(e) => onChange({ requiresVpn: e.target.checked })}
        />
        <Hint>{vpn.group.requiresHint}</Hint>
      </div>
      <NetworkFields listId={draft.networkListId} share={draft.shareVmNetwork} onChange={onChange} />
      <dl className="m-0 divide-y divide-muted/70 rounded-xl border border-muted p-4">
        <Line label={tasks.columns.taskType} step="type" onEdit={edit("type")}>{typeName}</Line>
        <Line label={tasks.columns.workflow} step="workflow" onEdit={edit("workflow")}>
          {draft.workflow ? t.workflowVersion(draft.workflow.name, draft.workflow.version.version) : null}
        </Line>
        <Line label={t.source} step="source" onEdit={edit("source")}>
          <span className="block">{tasks.source[draft.source]}</span>
          {draft.source !== "manual" ? (
            <span className="mt-1 block text-[13px] text-gray-500">
              {t.sourceVia(secretName(draft.credentialId), cadenceLabel(cadenceMinutes(draft.cadence) ?? 5))}
            </span>
          ) : null}
          {filterText(draft) ? <span className="mt-1.5 block">{filterText(draft)}</span> : null}
          {draft.source === "jira" && draft.jira.includeSubtasks ? <span className="mt-1 block text-[13px] text-gray-500">{tasks.create.jira.subtasksIncluded}</span> : null}
        </Line>
        <Line label={t.repositories} step="source" onEdit={edit("source")}>
          {repositories.length === 0 ? <span className="text-gray-500">{t.noRepositories}</span> : repositories.map((r) => <Mono key={r.repo} className="block">{r.default_branch ? `${r.repo} @ ${r.default_branch}` : r.repo}</Mono>)}
        </Line>
        <Line label={t.toolchains} step="access" onEdit={edit("access")}>
          {draft.toolchains.length === 0 ? <span className="text-gray-500">{t.noToolchains}</span> : draft.toolchains.map((tc) => toolchainNames[tc]).join(", ")}
        </Line>
        <Line label={t.secrets} step="access" onEdit={edit("access")}>
          {bound.length === 0 ? <span className="text-gray-500">{t.noSecrets}</span> : bound.map(({ index }) => <span key={index} className="block">{secretName(draft.bindings[index]!)}</span>)}
        </Line>
      </dl>
    </div>
  );
}
